import React, {useEffect, useRef, useState} from 'react';

import {Anchor, Heading, Icon, Menu, SplitCard} from '@albert-io/atomic';
import type {ClassroomModelV1} from '@albert-io/models';
import SubjectSplitCard from 'client/components/SubjectSplitCard/SubjectSplitCard.react';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import masqueradeStore from 'generic/Masquerade/Masquerade.store';
import type {List} from 'immutable';
import withTracking from 'lib/hocs/withTracking';
import {useTracking} from 'react-tracking';

import {getActiveClassroomsWithSubjectsQuery} from 'client/generic/SubjectMenuRenderer/helpers/queries';

import classnames from 'classnames';

import '../subject-menu-renderer.scss';

import type {SubjectCardData} from '../types';
import FreePracticeOptions from './FreePracticeOptions/FreePracticeOptions';
import './subject-course-card.scss';

const TrackedAnchor = withTracking(Anchor, {
  onClick: {
    event: 'click',
    type: 'dropdown',
    feature: 'Course Library',
    component: 'Subject Card',
    content: (e: React.MouseEvent<HTMLElement>) => (e.target as HTMLElement).dataset.trackingSubject
  }
});

interface SubjectCourseCardProps {
  activeClassrooms?: List<ClassroomModelV1>;
  className?: string;
  content: React.ReactNode;
  isAssessmentsOnly?: boolean;
  isUserLicensedTeacher?: boolean;
  isLoggedOutOrStudent?: boolean;
  subject: SubjectCardData;
  to: string;
}

const SubjectCourseCard = ({
  activeClassrooms,
  className,
  content,
  isAssessmentsOnly = false,
  isUserLicensedTeacher = false,
  isLoggedOutOrStudent = false,
  subject,
  to
}: SubjectCourseCardProps) => {
  const {trackEvent} = useTracking({component: 'Subject Card', feature: 'Course Library'});
  const [disableDropdown, setDisableDropdown] = useState(false);
  const [showDescription, setShowDescription] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const wasMenuOpenRef = useRef(false);
  const menuIsOpenRef = useRef(false);
  const linkRef = useRef<HTMLAnchorElement>(null);
  const descriptionRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (wasMenuOpenRef.current && !menuIsOpenRef.current && !isAssessmentsOnly) {
      setDisableDropdown(true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => setDisableDropdown(false), 500);
    }
    wasMenuOpenRef.current = menuIsOpenRef.current;
  });

  // Prevent users from reopening dropdown while invalidation is in process
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const disableDropdownBtn =
    !isLoggedOutOrStudent &&
    (!getActiveClassroomsWithSubjectsQuery().isResourceReady() || disableDropdown);

  const showActionBar = !isLoggedOutOrStudent || masqueradeStore.isMasquerading();

  return (
    <SubjectSplitCard
      className={classnames(className, {
        'subject-course-card--description-in-view': showDescription,
        'subject-course-card--action-bar-in-view': showActionBar
      })}
      key={subject.id}
    >
      <SubjectSplitCard.Top
        className={classnames(
          `subject-course-card__details flex items-end overflow-hidden p-x3 content-primary-inverse background-domain-${subject.curriculumAreaSlug}`
        )}
        onClick={(e: React.MouseEvent<HTMLElement>) => {
          const link = linkRef.current;
          const description = descriptionRef.current;
          if (link !== e.target && description !== e.target) {
            link?.click();
            trackEvent({
              event: 'click',
              type: 'button',
              content: subject.name
            });
          }
        }}
      >
        <SubjectSplitCard.Thumbnail xlinkHref={subject.logoHref} />
        <SubjectSplitCard.Title className='flex flex-col'>
          <Heading className={classnames({'subject-course-card__title': content})}>
            <Anchor
              // @ts-expect-error innerRef is a valid prop on react-router v3's Link, passed through Anchor's rest spread
              innerRef={linkRef}
              to={to}
              inheritColor
              data-testid={`subject-card-link-${subject.urlSlug}`}
            >
              {subject.name}
            </Anchor>
          </Heading>
          <Anchor
            as='button'
            onClick={() => setShowDescription((prev) => !prev)}
            inheritColor
            className='subject-course-card__description-link'
            role='switch'
            aria-checked={showDescription}
            ref={descriptionRef}
          >
            {showDescription ? 'Hide description' : 'Show description'}
          </Anchor>
        </SubjectSplitCard.Title>
      </SubjectSplitCard.Top>
      <SplitCard.Section
        aria-hidden={!showDescription}
        className='subject-course-card__description grow overflow-hidden px-x3'
      >
        <MarkdownRendererV2 text={content} />
      </SplitCard.Section>
      {showActionBar && (
        <SubjectSplitCard.Bottom
          paddingLevel={0}
          border='none'
          className='subject-course-card__action-bar flex justify-end px-x3! py-x1 text-right font-bold content-secondary'
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          <Menu
            position='bottom-start'
            positionFixed
            onToggleCallback={(isOpen: boolean) => {
              menuIsOpenRef.current = isOpen;
            }}
          >
            <Menu.Trigger>
              {(triggerProps) => (
                <TrackedAnchor
                  {...triggerProps}
                  as='button'
                  inheritColor
                  disabled={disableDropdownBtn}
                  data-tracking-subject={subject.name}
                >
                  Practice Settings
                  <Icon icon='caret-down' className='ml-x1' />
                </TrackedAnchor>
              )}
            </Menu.Trigger>
            <Menu.Content className='z-1 max-w-[32ch]'>
              <FreePracticeOptions
                className='subject-course-card__popper__dropdown-items text-left'
                classrooms={activeClassrooms}
                subjectId={subject.id}
                isAssessmentsOnly={isAssessmentsOnly}
                isUserLicensedTeacher={isUserLicensedTeacher}
              />
            </Menu.Content>
          </Menu>
        </SubjectSplitCard.Bottom>
      )}
    </SubjectSplitCard>
  );
};

export default SubjectCourseCard;
